// src/common/pages/authorValidation/PasoFoto.tsx
import React, { useEffect, useRef } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { CamaraDialog } from "../dniUpload/CamaraDialog";
import { PREVIEW_WIDTH } from "../dniUpload/useLecturaDni";
import type { LecturaDni } from "../dniUpload/useLecturaDni";
import { CATALOGO } from "../dniUpload/reader/catalogo";
import { frenteSinTramitePuedeSeguir } from "../dniUpload/reglasDeLaFoto";
import type { ModeloDeDocumento } from "../dniUpload/reader/catalogo";
import { CheckCircleIcon } from "../../../icons";
import type { TextosPagina } from "./textos";

type Lado = "anverso" | "reverso";

/**
 * Qué muestra la etapa de las fotos (KAN-460): el pedido de una cara, o las dos fotos aprobadas,
 * juntas. Lo decide la lectura (vistaDeLasFotos), no un paso guardado.
 */
export type VistaDeLasFotos = Lado | "ambas";

export interface PasoFotosProps {
  /** Lo que se muestra: la pantalla lo calcula con vistaDeLasFotos y lo sostiene mientras se procesa una foto. */
  vista: VistaDeLasFotos;
  /** El contenido del h2 del paso: la pantalla le antepone el paso, para el lector de pantalla. */
  titulo: React.ReactNode;
  lectura: LecturaDni;
  textos: TextosPagina;
  /** Los textos del DNI (avisos operativos: calidad, corte, lectura en curso). */
  T: Record<string, string>;
  onContinuar: () => void;
}


/**
 * Si en el frente que se entrega no queda el Nº de Trámite a la vista: quedó
 * tapado de verdad (el número impreso y el código de barras, ver
 * tramiteTapadoFrente en useLecturaDni), o el modelo reconocido no lo lleva en
 * el frente (el 2009 y el 2026). Un modelo sin nada en el frente con un código
 * de barras ubicado es una lectura incoherente —puede ser un 2012 mal
 * clasificado, con el número impreso a la vista— y no cuenta.
 */
export function frenteSinTramiteALaVista(lectura: LecturaDni): boolean {
  return (
    lectura.tramiteTapadoFrente ||
    (lectura.tapadoFrente.garantizado &&
      lectura.tapadoFrente.zonas.length === 0 &&
      lectura.zonasSensiblesAnverso.length === 0)
  );
}

/**
 * Si el frente cumple su regla del Nº de Trámite: quedó a salvo (frenteSinTramiteALaVista) o, sin
 * eso, la regla deja seguir igual (frenteSinTramitePuedeSeguir: hoy sí, por decisión del usuario).
 */
export function frenteEntregable(lectura: LecturaDni): boolean {
  return frenteSinTramiteALaVista(lectura) || frenteSinTramitePuedeSeguir();
}

/**
 * La cara de la que hay que pedir otra foto, o null si las dos se pueden entregar: primero la regla
 * de cada cara y después la aceptación de las dos juntas (aceptacion.ts: datos confiables, las dos
 * del mismo documento, los datos básicos legibles en alguna).
 */
export function caraAPedir(lectura: LecturaDni): "anverso" | "reverso" | null {
  // Una foto de la otra cara no cuenta como esta (recorrido del usuario): ni dos dorsos ni dos frentes.
  if (lectura.caraEquivocada.anverso) return "anverso";
  if (lectura.caraEquivocada.reverso) return "reverso";
  if (!frenteEntregable(lectura)) return "anverso";
  if (!dorsoEntregable(lectura)) return "reverso";
  return lectura.aceptacion?.pedirOtra ?? null;
}

/**
 * Si algún modelo del catálogo tiene una zona sensible MEDIDA en el dorso: solo
 * entonces hay algo que el tapado del dorso pueda garantizar. Sale del catálogo y
 * no de una lista: cuando se midan el 2009 y el 2026, el pedido de otra foto del
 * dorso se activa solo.
 */
export function algunDorsoSeTapa(catalogo: readonly ModeloDeDocumento[] = CATALOGO): boolean {
  return catalogo.some((m) => m.zonas.some((z) => z.cara === "dorso" && z.sensible && z.procedencia === "medida"));
}

/**
 * Si el dorso se puede entregar (KAN-455). Pide otra foto solo si hay algo que
 * proteger y esta foto no lo protegió: algún modelo del catálogo tiene una zona
 * sensible medida en el dorso (algunDorsoSeTapa) y el tapado de esta foto no
 * quedó garantizado porque no se ubicó la tarjeta o no se reconoció el modelo.
 * Hoy ningún modelo la tiene (el 2012 no lleva nada en el dorso; el 2009 y el
 * 2026, sin medir), así que el dorso nunca pide otra foto: bloquear no
 * protegería nada y trabaría fotos buenas (fondo claro, recorte al ras).
 *
 * Un modelo reconocido con la zona sin medir pasa siempre: no se tapa una zona
 * estimada (tapar de más puede borrar datos) y otra foto no cambiaría nada.
 */
export function dorsoEntregable(lectura: LecturaDni, catalogo: readonly ModeloDeDocumento[] = CATALOGO): boolean {
  if (!algunDorsoSeTapa(catalogo)) return true;
  return lectura.tapadoDorso.garantizado || lectura.tapadoDorso.motivo === "zona_sin_medir";
}


/** Las dos fotos se cruzaron: la del frente es del dorso y la del dorso, del frente. */
const cruzadas = (lectura: LecturaDni) => lectura.caraEquivocada.anverso && lectura.caraEquivocada.reverso;

/**
 * Si hay que pedir otra foto de esta cara, que ya tiene una: no cumple su regla, la aceptación de
 * las dos caras la señala (caraAPedir) o las dos fotos se cruzaron. El frente se juzga cuando
 * terminó de leerse; el dorso, con la foto, pero no mientras el hook sigue leyendo sus datos (el
 * CUIL y el domicilio se leen después de dar la foto por buena).
 */
function pedirOtraDe(lectura: LecturaDni, lado: Lado): boolean {
  if (lado === "anverso") {
    return lectura.anversoStatus === "read" && !!lectura.anversoEnviable && caraAPedir(lectura) === "anverso";
  }
  const conFoto = !!lectura.reversoEnviable && lectura.reversoStatus !== "processing";
  return conFoto && !lectura.extrayendo && (caraAPedir(lectura) === "reverso" || cruzadas(lectura));
}

/**
 * Si esta cara está aprobada: tiene su foto, leída (el frente, con su lectura terminada; el dorso
 * alcanza con la foto, aunque su banda no se haya podido leer) y no hay que pedir otra. Los avisos
 * operativos —calidad, banda ilegible— no la desaprueban: SADAIC pide la foto, no que una máquina
 * la lea; se muestran con las dos fotos juntas.
 */
export function caraAprobada(lectura: LecturaDni, lado: Lado): boolean {
  const conFoto =
    lado === "anverso"
      ? lectura.anversoStatus === "read" && !!lectura.anversoEnviable
      : !!lectura.reversoEnviable && lectura.reversoStatus !== "processing";
  return conFoto && !pedirOtraDe(lectura, lado);
}

/**
 * Qué se muestra de las fotos: con las dos aprobadas, las dos juntas; si no, el pedido del frente
 * y, aprobado el frente, el del dorso. Una cara que deja de cumplir (otra foto, la aceptación de
 * las dos juntas) vuelve a pedirse sola. Sin frente y con un frente en el lugar del dorso, el
 * dorso: ahí se ofrece usar esa foto como frente.
 */
export function vistaDeLasFotos(lectura: LecturaDni): VistaDeLasFotos {
  const frente = caraAprobada(lectura, "anverso");
  if (frente && caraAprobada(lectura, "reverso")) return "ambas";
  if (frente) return "reverso";
  const unFrenteEnElDorso = !lectura.anversoEnviable && !!lectura.reversoEnviable && lectura.caraEquivocada.reverso;
  return unFrenteEnElDorso ? "reverso" : "anverso";
}

/** El aviso de que la foto del frente parece de otro documento o de otra persona: no frena. */
function avisoDelDocumento(lectura: LecturaDni): string {
  const veredicto = lectura.verdictAnverso;
  return veredicto &&
    !veredicto.ok &&
    veredicto.reason &&
    lectura.ACCIONABLES.includes(veredicto.reason) &&
    lectura.vieneAlCaso(veredicto.reason)
    ? lectura.reasonMsg(veredicto.reason, veredicto.leido)
    : "";
}

interface DeUnaCara {
  lado: Lado;
  lectura: LecturaDni;
  textos: TextosPagina;
  T: Record<string, string>;
}

/** Si el hook está leyendo esta cara, o el resto del documento: mientras tanto no se cambia la foto. */
const leyendo = (lectura: LecturaDni, lado: Lado) =>
  (lado === "anverso" ? lectura.anversoStatus : lectura.reversoStatus) === "processing" || lectura.extrayendo;

/** La foto que se va a entregar, con la marca negra donde se tapó algo con certeza. */
const VistaPrevia: React.FC<Omit<DeUnaCara, "T">> = ({ lado, lectura, textos }) => (
  <canvas
    ref={lado === "anverso" ? lectura.previewAnversoRef : lectura.previewReversoRef}
    role="img"
    aria-label={lado === "anverso" ? textos.vistaPreviaFrente : textos.vistaPreviaDorso}
    style={{ width: PREVIEW_WIDTH, maxWidth: "100%", borderRadius: 8, display: "block" }}
  />
);

/** Quitar la marca negra de esta cara, si la lleva, o volver a ponerla. */
const BotonDeLaMarca: React.FC<Omit<DeUnaCara, "T">> = ({ lado, lectura, textos }) =>
  lectura.hayMarcaNegra[lado] ? (
    <Button size="small" disabled={leyendo(lectura, lado)} onClick={() => lectura.ponerMarca(lado, !lectura.marcaPuesta[lado])}>
      {lectura.marcaPuesta[lado] ? textos.quitarMarca : textos.ponerMarca}
    </Button>
  ) : null;

/** «Elegir una foto»: un botón de verdad, no un <label>: Enter y Espacio activan un botón, y sobre un label no hacen nada. */
const ElegirFoto: React.FC<Omit<DeUnaCara, "T"> & { size?: "small" }> = ({ lado, lectura, textos, size }) => {
  const selector = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button size={size} disabled={leyendo(lectura, lado)} onClick={() => selector.current?.click()}>
        {textos.elegirFoto}
      </Button>
      <input
        ref={selector}
        hidden
        type="file"
        accept="image/*"
        aria-label={textos.elegirFoto}
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          e.target.value = "";
          if (archivo) lectura.handleFoto(lado, archivo);
        }}
      />
    </>
  );
};

/** Los avisos que no frenan: la foto que no se pudo leer del todo, el documento que parece otro y la calidad. */
const AvisosDeLaCara: React.FC<DeUnaCara> = ({ lado, lectura, T }) => {
  const frente = lado === "anverso";
  const error = frente ? lectura.anversoError : lectura.reversoError;
  const estado = frente ? lectura.anversoStatus : lectura.reversoStatus;
  const conFoto = !!(frente ? lectura.anversoEnviable : lectura.reversoEnviable) && estado !== "processing";
  const calidad = conFoto ? lectura.motivosDeCalidad(frente ? lectura.calidadAnverso : lectura.calidadReverso) : [];
  const documento = frente ? avisoDelDocumento(lectura) : "";
  return (
    <>
      {error && (
        <Alert severity={conFoto ? "warning" : "error"} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {documento && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {documento}
        </Alert>
      )}
      {calidad.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">{T.calidad}</Typography>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {calidad.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
};

/**
 * El pedido de una cara: sacar la foto o elegirla. Si la que llegó no se puede entregar (caraAPedir:
 * la regla de la cara o la aceptación de las dos juntas), se ve con el pedido de otra, sin
 * explicaciones técnicas y sin tapado a mano; si es de la otra cara, se ofrece usarla como tal. La
 * salida, si nunca sale, es «No puedo usar mi DNI».
 */
const CaraPedida: React.FC<DeUnaCara> = (props) => {
  const { lado, lectura, textos } = props;
  const frente = lado === "anverso";
  const estado = frente ? lectura.anversoStatus : lectura.reversoStatus;
  const conFoto = !!(frente ? lectura.anversoEnviable : lectura.reversoEnviable) && estado !== "processing";
  const pedirOtra = pedirOtraDe(lectura, lado);
  // La foto de esta cara es la de la otra: si la otra falta, se ofrece usarla como tal.
  const equivocada = lectura.caraEquivocada[lado] && pedirOtra;
  const faltaLaOtra = frente ? !lectura.reversoEnviable : !lectura.anversoEnviable;
  const ofrecerLaOtra = equivocada && faltaLaOtra;
  const textoDelPedido = cruzadas(lectura)
    ? textos.cruzadas
    : equivocada
    ? frente
      ? faltaLaOtra ? textos.pareceDorso : textos.pareceDorsoFaltaFrente
      : faltaLaOtra ? textos.pareceFrente : textos.pareceFrenteFaltaDorso
    : frente
      ? textos.otraFotoDelFrente
      : textos.otraFotoDelDorso;

  // El canvas recién existe con la foto: se dibuja al aparecer. Esta parte se dibuja solo fuera de
  // la espera de la foto, así que también al terminar la espera (recorrido del usuario: la lectura
  // del dorso terminaba durante la espera, y al aparecer el canvas nadie lo dibujaba).
  useEffect(() => {
    if (conFoto) lectura.refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conFoto]);

  return (
    <>
      {conFoto && (
        <Box sx={{ mb: 2 }}>
          <VistaPrevia {...props} />
          <BotonDeLaMarca {...props} />
        </Box>
      )}
      {pedirOtra && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {textoDelPedido}
          {(ofrecerLaOtra || cruzadas(lectura)) && (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => (cruzadas(lectura) ? lectura.intercambiarCaras() : lectura.usarComoLaOtraCara(lado))}
              >
                {textos.si}
              </Button>
            </Box>
          )}
        </Alert>
      )}
      <AvisosDeLaCara {...props} />
      <Button
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 1 }}
        disabled={leyendo(lectura, lado)}
        onClick={() => lectura.setCamara(lado)}
      >
        {pedirOtra ? textos.sacarOtra : textos.sacarFoto}
      </Button>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
        <ElegirFoto {...props} />
      </Box>
    </>
  );
};

/**
 * Qué tapa la marca negra de esta cara, dicho tal cual: el Nº de Trámite, el código de barras o los
 * dos. Con la marca ya quitada, lo que queda a la vista (no se dice que «tapa» lo que ya no tapa).
 * Sin marca en la cara, nada.
 */
export function textoDeLaMarca(lectura: LecturaDni, lado: Lado, textos: TextosPagina): string {
  if (!lectura.hayMarcaNegra[lado]) return "";
  const { tramite, codigo } = lectura.queTapaLaMarca[lado];
  const puesta = lectura.marcaPuesta[lado];
  if (tramite && codigo) return puesta ? textos.marcaTapaTramiteYCodigo : textos.sinMarcaSeVeTramiteYCodigo;
  if (tramite) return puesta ? textos.marcaTapaTramite : textos.sinMarcaSeVeTramite;
  if (codigo) return puesta ? textos.marcaTapaCodigo : textos.sinMarcaSeVeCodigo;
  return "";
}

/**
 * Una de las dos fotos aprobadas: su nombre, la foto, la marca con lo que tapa, cambiarla y sus
 * avisos.
 */
const FotoAprobada: React.FC<DeUnaCara> = (props) => {
  const { lado, lectura, textos } = props;
  const nombre = `foto-aprobada-${lado}`;
  return (
    <Box role="group" aria-labelledby={nombre} sx={{ flex: "1 1 260px", maxWidth: PREVIEW_WIDTH, minWidth: 0 }}>
      <Typography id={nombre} variant="subtitle2" sx={{ mb: 0.5 }}>
        {lado === "anverso" ? textos.frente : textos.dorso}
      </Typography>
      <VistaPrevia {...props} />
      {/* Lo que tapa la marca; si se quitó, lo que queda a la vista. */}
      {textoDeLaMarca(lectura, lado, textos) && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {textoDeLaMarca(lectura, lado, textos)}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5, mb: 1 }}>
        <BotonDeLaMarca {...props} />
        <Button size="small" disabled={leyendo(lectura, lado)} onClick={() => lectura.setCamara(lado)}>
          {textos.sacarOtra}
        </Button>
        <ElegirFoto {...props} size="small" />
      </Box>
      <AvisosDeLaCara {...props} />
    </Box>
  );
};

/**
 * Las dos fotos aprobadas, juntas, y debajo «Continuar» (KAN-460). Cada una se puede cambiar, y la
 * marca negra se puede quitar en cada cara; debajo de cada foto con marca, una línea dice qué tapa
 * en esa cara. Mientras
 * el hook sigue leyendo los datos del dorso (CUIL, domicilio) no se continúa.
 */
const AmbasFotos: React.FC<Omit<DeUnaCara, "lado"> & { onContinuar: () => void }> = ({ onContinuar, ...props }) => {
  const { lectura, textos } = props;
  // Los dos canvas aparecen juntos al llegar a esta vista: se dibujan.
  useEffect(() => {
    lectura.refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
        <FotoAprobada lado="anverso" {...props} />
        <FotoAprobada lado="reverso" {...props} />
      </Box>
      <Button
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 1 }}
        disabled={lectura.extrayendo}
        onClick={onContinuar}
      >
        {textos.continuar}
      </Button>
    </>
  );
};

/** La señal de que la otra cara se aprobó: un tilde verde y su texto, sin la foto (KAN-460). */
const Aprobada: React.FC<{ texto: string }> = ({ texto }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <CheckCircleIcon aria-hidden="true" sx={{ fontSize: 40, color: "success.main" }} />
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {texto}
    </Typography>
  </Box>
);

/**
 * Las fotos del DNI (KAN-460). Se pide el frente; aprobado, se ve un tilde verde, sin la foto, y se
 * pide el dorso; aprobado el dorso, se ven las dos fotos juntas y «Continuar». Si una cara falla, se
 * sigue pidiendo otra de esa cara, con los mismos avisos de siempre. Una foto con las dos caras
 * pregunta antes de cortarse. Mientras se procesa una foto, la espera solo deja cancelar; las
 * cámaras de las dos caras viven acá, fuera de lo que cambia con la vista.
 */
export const PasoFotos: React.FC<PasoFotosProps> = ({ vista, titulo, lectura, textos, T, onContinuar }) => {
  const otra: Lado | null = vista === "anverso" ? "reverso" : vista === "reverso" ? "anverso" : null;
  const estado = vista === "reverso" ? lectura.reversoStatus : lectura.anversoStatus;
  // Primero se lee la foto; después, ya dada por buena, el resto de los datos del documento.
  const textoLeyendo = lectura.procesando
    ? textos.procesandoFoto
    : vista !== "ambas" && estado === "processing"
    ? T.procesando
    : lectura.extrayendo
    ? T.extrayendo
    : "";

  return (
    <Box>
      {/* tabIndex -1: la pantalla le pasa el foco al cambiar de paso. */}
      <Typography tabIndex={-1} variant="h6" component="h2" sx={{ mb: 2 }}>
        {titulo}
      </Typography>

      {otra && caraAprobada(lectura, otra) && (
        <Aprobada texto={otra === "anverso" ? textos.frenteAprobado : textos.dorsoAprobado} />
      )}

      {!lectura.procesando && lectura.propuesta && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {T.cortePregunta}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Button size="small" variant="contained" onClick={lectura.aceptarCorte}>
              {T.corteSi}
            </Button>
            <Button size="small" onClick={lectura.rechazarCorte}>
              {T.corteNo}
            </Button>
          </Box>
          <canvas
            ref={lectura.propuestaRef}
            role="img"
            aria-label={textos.propuestaDeCorte}
            style={{ width: PREVIEW_WIDTH, maxWidth: "100%", borderRadius: 4 }}
          />
        </Alert>
      )}

      {/* La región ya está cuando empieza la lectura: así el lector de pantalla anuncia que aparece el texto. */}
      <Box role="status">
        {textoLeyendo && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <CircularProgress size={20} aria-label={textoLeyendo} />
            <Typography variant="body2">{textoLeyendo}</Typography>
          </Box>
        )}
      </Box>

      {lectura.procesando ? (
        // Recorrido de KAN-452: mientras se procesa una foto, una espera que no deja operar. La cara
        // que se reemplaza ya se borró; la única acción es «Cancelar», que la deja vacía.
        <Button fullWidth variant="outlined" size="large" onClick={lectura.cancelar}>
          {textos.cancelar}
        </Button>
      ) : vista === "ambas" ? (
        <AmbasFotos lectura={lectura} textos={textos} T={T} onContinuar={onContinuar} />
      ) : (
        <CaraPedida lado={vista} lectura={lectura} textos={textos} T={T} />
      )}

      {(["anverso", "reverso"] as const).map((lado) => (
        <CamaraDialog
          key={lado}
          open={lectura.camara === lado}
          onClose={() => lectura.setCamara(null)}
          titulo={lado === "anverso" ? T.camaraFrente : T.camaraDorso}
          cara={lado === "anverso" ? "frente" : "dorso"}
          onCaptura={(foto, pista) => lectura.handleFoto(lado, foto, pista)}
        />
      ))}
    </Box>
  );
};
